import React, { useRef, useState, type KeyboardEvent } from 'react';
import { parseDateOnly, toDateOnly } from './date';
import {
    MiniCalendarHeader,
    type CalendarView,
    type GridView,
    type PagingNav
} from './MiniCalendarHeader';
import {
    YEAR_PAGE_SIZE,
    clampMonth,
    isYearOutOfBounds,
    monthIndex,
    resolveMonthBounds,
    yearPageStart
} from './monthBounds';
import { MonthGrid, YearGrid } from './MonthYearGrids';
import { buildMonthCells } from './monthCells';
import { cellStateClass } from './cellStateClass';
import type { CalendarMonth } from './types';

const DAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MAX_COUNT_DOTS = 3;

export interface MiniCalendarProps {
    month: CalendarMonth;
    onMonthChange: (month: CalendarMonth) => void;
    /** date-only ('YYYY-MM-DD') -> non-canceled appointment count. */
    counts: Record<string, number>;
    selected: string | null;
    onSelect: (day: string | null) => void;
    todayIso: string;
    /** Locks the whole calendar (cells + month nav) while a composer or
     *  detail panel is open elsewhere, instead of half-interactively retargeting it. */
    disabled?: boolean;
    /** Clamp month navigation to the host's fetched data window. */
    minMonth?: CalendarMonth;
    maxMonth?: CalendarMonth;
    /** Earliest pickable day ('YYYY-MM-DD'); earlier days render disabled. */
    minDate?: string;
}

type DayGridProps = Pick<
    MiniCalendarProps,
    'month' | 'counts' | 'selected' | 'onSelect' | 'todayIso' | 'disabled' | 'minDate'
>;

function DayGrid({ month, counts, selected, onSelect, todayIso, disabled, minDate }: DayGridProps) {
    const cells = buildMonthCells(month.year, month.month);
    return (
        <div
            className={`grid grid-cols-7 gap-y-1 text-center ${disabled ? 'opacity-40 pointer-events-none' : ''}`}
        >
            {DAY_INITIALS.map((label, index) => (
                <div
                    key={`${label}-${index}`}
                    className="text-[10px] font-medium uppercase tracking-wide text-gray-400 pb-1"
                >
                    {label}
                </div>
            ))}
            {cells.map((day, index) => {
                if (!day) return <div key={`empty-${index}`} className="h-10" />;
                const key = toDateOnly(new Date(month.year, month.month, day));
                const count = counts[key] || 0;
                const isToday = key === todayIso;
                const isSelected = key === selected;
                // Date-only strings compare in calendar order.
                const beforeMin = !!minDate && key < minDate;
                const stateClass = cellStateClass({
                    selected: isSelected,
                    outOfRange: beforeMin,
                    current: isToday
                });
                return (
                    <button
                        type="button"
                        key={key}
                        onClick={() => onSelect(isSelected ? null : key)}
                        disabled={disabled || beforeMin}
                        className={`relative mx-auto h-10 w-10 text-sm rounded-lg transition-colors disabled:cursor-not-allowed ${stateClass}`}
                    >
                        {day}
                        {!!count && (
                            <span
                                className="absolute left-1/2 -translate-x-1/2 bottom-1 rounded-full bg-yellow-400"
                                style={{
                                    width: Math.min(count, MAX_COUNT_DOTS) * 4 + 2,
                                    height: 3
                                }}
                            />
                        )}
                    </button>
                );
            })}
        </div>
    );
}

export const MiniCalendar: React.FC<MiniCalendarProps> = ({
    month,
    onMonthChange,
    counts,
    selected,
    onSelect,
    todayIso,
    disabled,
    minMonth,
    maxMonth,
    minDate
}) => {
    const [view, setView] = useState<CalendarView>('days');
    const [yearPage, setYearPage] = useState(() => yearPageStart(month.year));
    const monthToggleRef = useRef<HTMLButtonElement>(null);
    const yearToggleRef = useRef<HTMLButtonElement>(null);
    const toggleRefs = { months: monthToggleRef, years: yearToggleRef };
    const bounds = resolveMonthBounds(minMonth, maxMonth, minDate);
    const todayDate = parseDateOnly(todayIso);
    const today = { year: todayDate.getFullYear(), month: todayDate.getMonth() };
    const atMin = bounds.min !== null && monthIndex(month) <= bounds.min;
    const atMax = bounds.max !== null && monthIndex(month) >= bounds.max;

    const shiftMonth = (delta: number) => {
        const shifted = new Date(month.year, month.month + delta, 1);
        onMonthChange({ year: shifted.getFullYear(), month: shifted.getMonth() });
    };

    // A view switch unmounts the focused grid cell; a mounted toggle keeps focus (and Escape) inside.
    const switchView = (next: CalendarView, focusOn: GridView) => {
        setView(next);
        toggleRefs[focusOn].current?.focus();
    };

    const toggleView = (next: GridView) => {
        if (view === next) {
            switchView('days', next);
            return;
        }
        if (next === 'years') setYearPage(yearPageStart(month.year));
        switchView(next, next);
    };

    const pickMonth = (picked: CalendarMonth, grid: GridView) => {
        onMonthChange(clampMonth(picked, bounds));
        switchView('days', grid);
    };

    // Escape steps back to the days instead of reaching the host popover's own close.
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Escape' || view === 'days') return;
        e.stopPropagation();
        switchView('days', view);
    };

    const daysNav: PagingNav = {
        unit: 'month',
        onPrev: () => shiftMonth(-1),
        onNext: () => shiftMonth(1),
        prevDisabled: atMin,
        nextDisabled: atMax
    };
    const yearsNav: PagingNav = {
        unit: 'years',
        onPrev: () => setYearPage((page) => page - YEAR_PAGE_SIZE),
        onNext: () => setYearPage((page) => page + YEAR_PAGE_SIZE),
        prevDisabled: isYearOutOfBounds(yearPage - 1, bounds),
        nextDisabled: isYearOutOfBounds(yearPage + YEAR_PAGE_SIZE, bounds)
    };
    const navByView: Record<CalendarView, PagingNav | null> = {
        days: daysNav,
        months: null,
        years: yearsNav
    };

    return (
        <div onKeyDown={handleKeyDown}>
            <MiniCalendarHeader
                month={month}
                view={view}
                disabled={disabled}
                onToggleView={toggleView}
                toggleRefs={toggleRefs}
                nav={navByView[view]}
            />
            {/* The day grid always holds the space so every view shares one size. */}
            <div className="relative">
                <div className={view === 'days' ? undefined : 'invisible'}>
                    <DayGrid
                        month={month}
                        counts={counts}
                        selected={selected}
                        onSelect={onSelect}
                        todayIso={todayIso}
                        disabled={disabled}
                        minDate={minDate}
                    />
                </div>
                {view === 'months' && (
                    <div className="absolute inset-0">
                        <MonthGrid
                            month={month}
                            today={today}
                            bounds={bounds}
                            disabled={disabled}
                            onPickMonth={(picked) =>
                                pickMonth({ year: month.year, month: picked }, 'months')
                            }
                        />
                    </div>
                )}
                {view === 'years' && (
                    <div className="absolute inset-0">
                        <YearGrid
                            month={month}
                            today={today}
                            bounds={bounds}
                            disabled={disabled}
                            pageStart={yearPage}
                            onPickYear={(year) => pickMonth({ year, month: month.month }, 'years')}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MiniCalendar;
